import Vue from 'vue';
import { flattenDeep } from 'lodash';
import { ApiServiceError } from '@tokenyico/vuejs-toolkit/src/exports/store/tokeny';

const { VUE_APP_API_URL_QUALIFICATION } = process.env;
const { VUE_APP_QUALIFICATION_ID } = process.env;
const { VUE_APP_URL_ONCHAINID_API } = process.env;

export class QualificiationStoreError extends Error {
  constructor ({ message = 'QualificiationStoreError', code, data }) {
    super(message);
    this.message = message;
    this.code = code;
    this.data = data;
  }
};

const emptyState = () => ({
  currentSection: null,
  documentLinks: {},
  documentUrls: {},
  investor: null,
  investorAllowed: null,
  kycStatus: null,
  languages: [],
  investorTypes: [],
  missingDisclaimers: null,
  noDisclaimer: null,
  qualification: null,
  signUpInfo: {
    email: null,
    investorType: null,
    country: null
  },
  userEmail: null,
  tokenInfo: null
});

export default {
  namespaced: true,
  state: emptyState,
  // getters are synchronous and should not throw errors
  getters: {
    documentLinkByDocumentId: (state) => ({ documentId }) => state.documentLinks[documentId],
    documentUrlByFieldsetId: (state) => ({ fieldsetId }) => state.documentUrls[fieldsetId],
    languages: (state) => state.languages,
    investorTypes: (state) => state.investorTypes,
    qualification: (state) => state.qualification,
    investor: (state) => state.investor,
    investorAllowed: (state) => state.investorAllowed,
    missingDisclaimers: (state) => state.missingDisclaimers,
    noDisclaimer: (state) => state.noDisclaimer,
    milestones: (state) => state.qualification && state.qualification.milestones ? state.qualification.milestones : [],
    sections: (state, getters) => flattenDeep(getters.milestones.map(milestone => milestone.sections)),
    completedSections: (state, getters) => getters.sections.filter(section => section.status === 'complete' || section.status === 'locked'),
    progress: (state, getters) => getters.completedSections.length > 0 ? Number((getters.completedSections.length * 100 / getters.sections.length).toFixed(0)) : 0,
    activeMilestone: (state, getters) => getters.milestones.find(m => m.status === 'active'),
    activeSection: (state, getters) => {
      if (!getters.activeMilestone) return;
      if (!getters.activeMilestone.sections || !getters.activeMilestone.sections.length) return getters.activeMilestone;
      return getters.activeMilestone.sections.find(s => s.status === 'active');
    },
    currentSection: (state) => state.currentSection,
    kycStatus: (state) => {
      if (state.kycStatus === 'Not_checked' && state.qualification && state.qualification.investorStatus === 'pending') {
        return 'Pending';
      }
      return state.kycStatus;
    },
    userEmail: (state) => state.userEmail,
    signUpInfo: (state) => state.signUpInfo,
    tokenInfo: (state) => state.tokenInfo,
    investorPortalUrl: (state) => state.qualification.investorPortalUrl
  },
  // actions can be async and can throw errors
  actions: {
    async fetch ({ dispatch, rootGetters }, { api = VUE_APP_API_URL_QUALIFICATION, path, externalPath = null, method = 'GET', headers = {}, body = null, queryParams = {}, auth = true }) {
      let action = 'tokeny/callAPI';
      if (!auth) {
        action = 'tokeny/fetch';
      }
      const actualPath = externalPath || `/qualification/${VUE_APP_QUALIFICATION_ID}${path}`;
      const locale = auth ? rootGetters['locale/locale'] : undefined;
      if (locale) {
        headers['Accept-Language'] = locale;
      }
      return dispatch(action, {
        api, path: actualPath, method, headers, body, queryParams
      }, { root: true });
    },
    addDocumentLink (state, payload) {
      state.commit('appendDocumentLink', payload);
    },
    addDocumentUrl (state, payload) {
      state.commit('appendDocumentUrl', payload);
    },
    removeDocumentLink (state, payload) {
      state.commit('clearDocumentLink', payload);
    },
    removeDocumentUrl (state, payload) {
      state.commit('clearDocumentUrl', payload);
    },
    async setUserEmail ({ commit }, userEmail) {
      commit('setUserEmail', userEmail);
    },
    async setSignUpInfo ({ commit }, signUpInfo) {
      commit('setSignUpInfo', signUpInfo);
    },
    async fetchInvestorAllowed ({ commit, dispatch }) {
      let investorAllowed = null;
      try {
        await dispatch('fetch', { path: '/investor/allowed' });
        investorAllowed = 'OK';
      } catch (error) {
        if (error instanceof ApiServiceError &&
          (error.statusCode === 403 || error.statusCode === 404) &&
          error.code &&
          ['ERROR_IDENTITY_NOT_COMPLETE', 'ERROR_COUNTRY_NOT_ALLOWED'].includes(error.code)) {
          investorAllowed = error.code;
        } else {
          throw error;
        }
      }
      commit('setInvestorAllowed', investorAllowed);
      return investorAllowed;
    },
    async newInvestor ({ commit, dispatch }, { country, investorType }) {
      const newInvestor = await dispatch('fetch', {
        method: 'POST',
        externalPath: '/investor',
        body: { country, investorType }
      });
      commit('setInvestor', newInvestor);
    },
    async fetchMissingDisclaimers ({ commit, dispatch }) {
      const res = await dispatch('fetch', { path: '/disclaimers' });
      if (res && res.disclaimers && Array.isArray(res.disclaimers.elements)) {
        commit('setMissingDisclaimers', res.disclaimers.elements.filter(d => d.value !== true));
      } else {
        throw new QualificiationStoreError({ code: 'UNEXPECTED' });
      }
      if (res.disclaimers.elements.length === 0 && this.state.qualification.noDisclaimer === null) {
        commit('setNoDisclaimer', true);
      }
    },
    async saveDisclaimers ({ commit, dispatch }, { disclaimers }) {
      await dispatch('fetch', { method: 'POST', path: '/disclaimers', body: { elements: disclaimers } });
      commit('setMissingDisclaimers', disclaimers.filter(d => d.value !== true));
    },
    async fetchQualification ({ commit, dispatch }) {
      const qualification = await dispatch('fetch', { path: '/' });
      commit('setQualification', qualification);
    },
    async fetchLanguages ({ commit, dispatch }) {
      const res = await dispatch('fetch', { path: '/languages', auth: false });
      const languages = res ? res.languages : res;
      commit('setLanguages', languages);
    },
    async fetchInvestorTypes ({ commit, dispatch }) {
      const res = await dispatch('fetch', { path: '/investor-types', auth: false });
      const investorTypes = res ? res.investorTypes : res;
      commit('setInvestorTypes', investorTypes);
    },
    async fetchKyc ({ commit, dispatch }) {
      const res = await dispatch('fetch', { path: '/kyc' });
      const kycStatus = res[0] ? res[0].status.charAt(0).toUpperCase() + res[0].status.slice(1) : 'Not_checked';
      commit('setKycStatus', kycStatus);
    },
    async fetchCurrentSection ({ commit, dispatch }, sectionId) {
      // isLoading computeds and route / sectionId watches depend on this so we need to set to null while fetching the next currentSection
      commit('setCurrentSection', null);
      const currentSection = await dispatch('fetch', { path: `/section/${sectionId}` });
      commit('setCurrentSection', currentSection);
    },
    async fetchCurrentSectionSilently ({ dispatch }, sectionId) {
      return await dispatch('fetch', { path: `/section/${sectionId}` });
    },
    async saveSection ({ commit, dispatch }, { sectionId, elements }) {
      await dispatch('fetch', { method: 'POST', path: `/section/${sectionId}`, body: { elements } });
      // @todo AG: should we commit something here? empty response expected
    },
    async fetchDocumentLink ({ commit, dispatch }, { documentId }) {
      const documentLink = await dispatch('fetch', { path: `/documents/download/${documentId}` });
      commit('appendDocumentLink', { documentId, documentLink });
    },
    async fetchDocumentUrl ({ commit, dispatch }, { fieldsetId, files }) {
      const documentUrl = await dispatch('fetch', { method: 'POST', path: '/investor/documents', body: { fieldsetId, files } });
      commit('appendDocumentUrl', { fieldsetId, documentUrl });
    },
    async fetchTokenInfo ({ commit, dispatch }) {
      let tokenInfo;
      try {
        tokenInfo = await dispatch('fetch', { path: '/token' });
      } catch (error) {
        if (!error.statusCode || error.statusCode !== 404 || !error.code || error.code !== 'ERROR_TREX_DATA_SERVICE_FETCH') {
          throw error;
        }
      }
      commit('setTokenInfo', tokenInfo);
    },
    async investorLanguage ({ dispatch }, language) {
      await dispatch('fetch', { method: 'POST', externalPath: '/investor/language', body: { language } });
    },
    reset ({ commit }) {
      commit('reset');
    },
    // onchainid API
    async linkWalletToIdentity ({ dispatch }, body) {
      await dispatch('fetch', { api: VUE_APP_URL_ONCHAINID_API, method: 'POST', externalPath: '/wallets', body });
    }
  },
  mutations: {
    reset (state) {
      const newState = emptyState();
      for (const key of Object.keys(newState)) {
        if (key === 'languages') continue; // keep this one
        Vue.set(state, key, newState[key]);
      }
    },
    appendDocumentLink (state, { documentId, documentLink }) {
      Vue.set(state.documentLinks, documentId, documentLink);
    },
    appendDocumentUrl (state, { fieldsetId, documentUrl }) {
      Vue.set(state.documentUrls, fieldsetId, documentUrl);
    },
    clearDocumentLink (state, { documentId }) {
      Vue.set(state.documentLinks, documentId, null);
    },
    clearDocumentUrl (state, { fieldsetId }) {
      Vue.set(state.documentUrls, fieldsetId, null);
    },
    setCurrentSection (state, currentSection) {
      state.currentSection = currentSection;
    },
    setInvestor (state, investor) {
      state.investor = investor;
    },
    setInvestorAllowed (state, investorAllowed) {
      state.investorAllowed = investorAllowed;
    },
    setKycStatus (state, kycStatus) {
      state.kycStatus = kycStatus;
    },
    setLanguages (state, languages) {
      Vue.set(state, 'languages', languages);
    },
    setInvestorTypes (state, investorTypes) {
      Vue.set(state, 'investorTypes', investorTypes);
    },
    setMissingDisclaimers (state, missingDisclaimers) {
      state.missingDisclaimers = missingDisclaimers;
    },
    setNoDisclaimer (state, noDisclaimer) {
      state.noDisclaimer = noDisclaimer;
    },
    setQualification (state, qualification) {
      Vue.set(state, 'qualification', qualification);
    },
    setSignUpInfo (state, signUpInfo) {
      state.signUpInfo = signUpInfo;
    },
    setUserEmail (state, userEmail) {
      state.userEmail = userEmail;
    },
    setTokenInfo (state, tokenInfo) {
      state.tokenInfo = tokenInfo;
    }
  }
};
